import React, { useCallback, useContext, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as styles from './dashboard.styles';
import ActivityIndicator from '../../core/components/activity-indicator/activity-indicator.component';
import BackButton from '../../core/components/back-button/back-button.component';
import { Center } from '../../core/components/center/center.component';
import Column from '../../core/components/column/column.component';
import { CrossAxisAlignment } from '../../core/components/flex/flex.component';
import { IsMobileContext } from '../../core/context/is-mobile';
import RouteConstants from '../../core/routes.constants';
import StyleConstants from '../../core/styles.constants';
import { useAllWhyellowAndAdminUsers } from '../authentication/hooks/users.hook';
import type User from '../authentication/models/user';
import DashboardUserCard from './cards/dashboard-user-card.component';
import CommonStyles from '../../core/common.styles';
import Grid from '../../core/components/grid/grid.component';

const UserDashboard = (): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const usersState = useAllWhyellowAndAdminUsers();
  const isMobile = useContext(IsMobileContext);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<User[] | undefined>(undefined);

  const navigateToOverview = useCallback((): void => {
    navigate(`/${RouteConstants.PATH_DASHBOARD}`);
  }, [navigate]);

  const didDeleteUser = useCallback(
    (deletedUser: User): void => {
      setUsers(users?.filter((x) => x.id !== deletedUser.id));
    },
    [setUsers, users],
  );

  if (isLoading && usersState.users != null) {
    setIsLoading(false);
    setUsers(usersState.users);
  }

  return (
    <div className={style(CommonStyles.pageMargin)}>
      <div className={style(styles.cornerButton(isMobile))}>
        <BackButton title={t('dashboard')} navigate={navigateToOverview} />
      </div>
      <Column className={style(CommonStyles.bodyMargin(isMobile))} crossAxisAlignment={CrossAxisAlignment.Stretch}>
        <div className={style(CommonStyles.sectionCard(isMobile))}>
          <Column>
            <span className={style(styles.columnHeader(isMobile))}>{t('administrator-accounts')}</span>
            {((): React.JSX.Element => {
              if (isLoading) {
                return (
                  <Center>
                    <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
                  </Center>
                );
              }

              if (users == null || users?.length === 0) {
                return <span className={style(styles.emptyListMessage)}>{t('no-admins-available')}</span>;
              }

              return (
                <Grid
                  rowClass={style(styles.gridRow)}
                  numberOfColumns={isMobile ? 1 : 3}
                  itemSpacing={12}
                  numberOfItems={users.length}
                  renderItem={(index): React.JSX.Element | undefined => {
                    if (users) {
                      const user = users[index];
                      return (
                        user && (
                          <div className={isMobile ? style(styles.rowItem(true)) : undefined} key={user.id}>
                            <DashboardUserCard user={user} handleDidDeleteUser={didDeleteUser} />
                          </div>
                        )
                      );
                    }
                    return undefined;
                  }}
                />
              );
            })()}
          </Column>
        </div>
      </Column>
    </div>
  );
};

export default UserDashboard;
